import { RefObject, useEffect } from 'react'

import { ITranscription, ITranscriptions } from '@/types/task.interface'

interface IUseTranscriptSelectionShortcut {
	enabled: boolean
	isBlocked: boolean
	transcriptions: ITranscriptions | null
	containerRef: RefObject<HTMLDivElement>
	translatedRef: RefObject<HTMLDivElement>
	selectSegmentFromTranscript: (
		container: HTMLElement,
		type: 'original' | 'translation',
		items: ITranscription[]
	) => void
}

export const useTranscriptSelectionShortcut = ({
	enabled,
	isBlocked,
	transcriptions,
	containerRef,
	translatedRef,
	selectSegmentFromTranscript
}: IUseTranscriptSelectionShortcut) => {
	useEffect(() => {
		if (!enabled) return

		const getSelectedTranscriptContainer = () => {
			const selection = window.getSelection()

			if (!transcriptions) return null

			if (
				!selection ||
				selection.isCollapsed ||
				!selection.anchorNode ||
				!selection.focusNode
			) {
				return null
			}

			if (
				containerRef.current?.contains(selection.anchorNode) &&
				containerRef.current.contains(selection.focusNode)
			) {
				return {
					element: containerRef.current,
					type: 'original' as const,
					items: transcriptions.transcriptions.original
				}
			}

			if (
				translatedRef.current?.contains(selection.anchorNode) &&
				translatedRef.current.contains(selection.focusNode)
			) {
				return {
					element: translatedRef.current,
					type: 'translation' as const,
					items: transcriptions.transcriptions.translation
				}
			}

			return null
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if ((event.ctrlKey || event.metaKey) && event.code === 'KeyC') {
				if (isBlocked) return

				const selectedContainer = getSelectedTranscriptContainer()

				if (!selectedContainer) return

				selectSegmentFromTranscript(
					selectedContainer.element,
					selectedContainer.type,
					selectedContainer.items
				)
			}
		}

		window.addEventListener('keydown', onKeyDown)

		return () => {
			window.removeEventListener('keydown', onKeyDown)
		}
	}, [
		enabled,
		isBlocked,
		transcriptions,
		containerRef,
		translatedRef,
		selectSegmentFromTranscript
	])
}
